import { useMutation, useQuery, skipToken } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { getPeople } from '@/services/db/customer/queries';
import { customerKeys, type Customer } from '@/services/db/customer/entity';
import { isEmptyString } from '@/utils/string';

/** How many profiles a search offers to pick from. */
const RECIPIENT_RESULT_LIMIT = 5;

/**
 * Sending one event, and finding the profile to send it for.
 *
 * Both reads are the modal's own: nothing else asks for a handful of
 * profiles by email address or by one condition, and the send is a one-off
 * with nothing to invalidate — the event is recorded asynchronously, which is
 * why callers are told about it on a delay rather than immediately.
 */

interface SendEventVars {
  environmentId: string;
  /** Internal CIO id of the recipient profile. */
  cioId: string;
  name: string;
  /** JSON-encoded event attributes ("" when none). */
  data: string;
}

/** Send a test event to a profile — `POST /v1/environments/:id/events`. @public */
export function useSendEvent() {
  return useMutation({
    mutationFn: ({ environmentId, cioId, name, data }: SendEventVars) =>
      hydraUiApi.events.create({
        params: { path: { environment_id: environmentId } },
        body: { ids: [cioId], name, data },
      }),
  });
}

/**
 * What a recipient search asks the customers endpoint: an email address, or
 * one encoded attribute condition. The endpoint treats the two as exclusive,
 * so a search is always exactly one of them.
 */
export type RecipientQuery = { email: string } | { filters: string };

/** A query worth sending: a blank term asks for nothing. */
function activeParams(query: RecipientQuery | null): RecipientQuery | null {
  if (query == null) {
    return null;
  }
  const term = 'email' in query ? query.email.trim() : query.filters;
  if (isEmptyString(term)) {
    return null;
  }
  return 'email' in query ? { email: term } : query;
}

/**
 * The profiles matching a recipient search, capped at what the picker shows.
 *
 * No query, or a blank email term, asks for nothing: the endpoint reads an
 * absent `email` as "every profile", which is not what an empty search box
 * means.
 */
export function useRecipientSearch(workspaceId: number, query: RecipientQuery | null) {
  const params = activeParams(query);
  const isActive = workspaceId > 0 && params != null;

  const result = useQuery({
    queryKey: isActive ? customerKeys.people(workspaceId, params) : ['customer', 'people', 'recipient-search', 'idle'],
    queryFn: isActive ? () => getPeople(workspaceId, params) : skipToken,
  });

  const customers: Customer[] = (result.data?.customers ?? []).slice(0, RECIPIENT_RESULT_LIMIT);

  return { customers, isSearching: isActive && result.isFetching, hasSearched: isActive && result.isSuccess };
}
